import { Injectable, signal } from '@angular/core';
import { Consultation, Symptom, ConsultationQuestion } from '../models/consultation.model';

@Injectable({
  providedIn: 'root'
})
export class ConsultationService {
  private consultations = signal<Consultation[]>([]);

  // Predefined symptoms library
  commonSymptoms: Symptom[] = [
    { id: '1', name: 'Fever', category: 'General' },
    { id: '2', name: 'Cold', category: 'Respiratory' },
    { id: '3', name: 'Cough', category: 'Respiratory' },
    { id: '4', name: 'Headache', category: 'Neurological' },
    { id: '5', name: 'Body Pain', category: 'Musculoskeletal' },
    { id: '6', name: 'Nausea', category: 'Digestive' },
    { id: '7', name: 'Vomiting', category: 'Digestive' },
    { id: '8', name: 'Diarrhea', category: 'Digestive' },
    { id: '9', name: 'Chest Pain', category: 'Cardiovascular' },
    { id: '10', name: 'Shortness of Breath', category: 'Respiratory' },
    { id: '11', name: 'Rash', category: 'Skin' },
    { id: '12', name: 'Dizziness', category: 'Neurological' },
    { id: '13', name: 'Fatigue', category: 'General' },
    { id: '14', name: 'Sore Throat', category: 'Respiratory' },
    { id: '15', name: 'Runny Nose', category: 'Respiratory' },
  ];

  // Smart questions based on symptoms
  consultationQuestions: ConsultationQuestion[] = [
    {
      id: 'q1',
      question: 'How long have you been experiencing these symptoms?',
      type: 'text',
      category: 'symptom',
      suggestedBy: ['Fever', 'Cold', 'Cough', 'Headache']
    },
    {
      id: 'q2',
      question: 'Do you have any allergies to medications?',
      type: 'yesno',
      category: 'allergy',
      suggestedBy: ['Fever', 'Cold', 'Cough', 'Rash']
    },
    {
      id: 'q3',
      question: 'Any family history of heart disease?',
      type: 'yesno',
      category: 'family_history',
      suggestedBy: ['Chest Pain', 'Shortness of Breath']
    },
    {
      id: 'q4',
      question: 'Any family history of diabetes?',
      type: 'yesno',
      category: 'family_history',
      suggestedBy: ['Fatigue', 'Frequent Urination']
    },
    {
      id: 'q5',
      question: 'Are you currently taking any medications?',
      type: 'yesno',
      category: 'medication',
      suggestedBy: ['Fever', 'Cold', 'Cough', 'Headache']
    },
    {
      id: 'q6',
      question: 'Do you smoke or consume alcohol?',
      type: 'yesno',
      category: 'lifestyle',
      suggestedBy: ['Cough', 'Shortness of Breath', 'Chest Pain']
    },
    {
      id: 'q7',
      question: 'Rate your pain level (1-10)',
      type: 'scale',
      category: 'symptom',
      suggestedBy: ['Headache', 'Body Pain', 'Chest Pain']
    },
    {
      id: 'q8',
      question: 'Any recent travel history?',
      type: 'yesno',
      category: 'lifestyle',
      suggestedBy: ['Fever', 'Diarrhea', 'Vomiting']
    },
    {
      id: 'q9',
      question: 'Any family history of hypertension?',
      type: 'yesno',
      category: 'family_history',
      suggestedBy: ['Headache', 'Dizziness', 'Chest Pain']
    },
    {
      id: 'q10',
      question: 'Have you had similar symptoms before?',
      type: 'yesno',
      category: 'symptom',
      suggestedBy: ['Fever', 'Cold', 'Cough', 'Headache']
    }
  ];

  // Prescription suggestions based on symptoms
  getPrescriptionSuggestions(symptoms: Symptom[]): any[] {
    const suggestions: any[] = [];
    const symptomNames = symptoms.map(s => s.name.toLowerCase());

    if (symptomNames.includes('fever') || symptomNames.includes('cold')) {
      suggestions.push({
        medication: 'Paracetamol',
        dosage: '500mg',
        frequency: 'Every 6 hours',
        duration: '3-5 days',
        reason: 'For fever and pain relief'
      });
    }

    if (symptomNames.includes('cold') || symptomNames.includes('cough') || symptomNames.includes('runny nose')) {
      suggestions.push({
        medication: 'Cetirizine',
        dosage: '10mg',
        frequency: 'Once daily',
        duration: '5 days',
        reason: 'For cold and allergy symptoms'
      });
    }

    if (symptomNames.includes('cough')) {
      suggestions.push({
        medication: 'Dextromethorphan',
        dosage: '15mg',
        frequency: 'Every 8 hours',
        duration: '5 days',
        reason: 'For cough suppression'
      });
    }

    if (symptomNames.includes('headache') || symptomNames.includes('body pain')) {
      suggestions.push({
        medication: 'Ibuprofen',
        dosage: '400mg',
        frequency: 'Every 8 hours',
        duration: '3 days',
        reason: 'For pain and inflammation'
      });
    }

    if (symptomNames.includes('sore throat')) {
      suggestions.push({
        medication: 'Amoxicillin',
        dosage: '500mg',
        frequency: 'Every 8 hours',
        duration: '7 days',
        reason: 'For bacterial throat infection'
      });
    }

    if (symptomNames.includes('nausea') || symptomNames.includes('vomiting')) {
      suggestions.push({
        medication: 'Ondansetron',
        dosage: '4mg',
        frequency: 'Every 8 hours',
        duration: '3 days',
        reason: 'For nausea and vomiting'
      });
    }

    if (symptomNames.includes('diarrhea')) {
      suggestions.push({
        medication: 'Loperamide',
        dosage: '2mg',
        frequency: 'After each loose stool',
        duration: '2 days',
        reason: 'For diarrhea control'
      });
    }

    return suggestions;
  }

  // Get suggested questions based on selected symptoms
  getSuggestedQuestions(symptoms: Symptom[]): ConsultationQuestion[] {
    const symptomNames = symptoms.map(s => s.name);
    return this.consultationQuestions.filter(q => 
      q.suggestedBy?.some(symptom => symptomNames.includes(symptom))
    );
  }

  getConsultations() {
    return this.consultations.asReadonly();
  }

  getConsultationById(id: string): Consultation | undefined {
    return this.consultations().find(c => c.id === id);
  }

  addConsultation(consultation: Omit<Consultation, 'id' | 'createdAt' | 'updatedAt'>): Consultation {
    const newConsultation: Consultation = {
      ...consultation,
      id: Date.now().toString(),
      createdAt: new Date(),
      updatedAt: new Date()
    };
    this.consultations.update(consultations => [...consultations, newConsultation]);
    return newConsultation;
  }

  updateConsultation(id: string, updates: Partial<Consultation>): void {
    this.consultations.update(consultations =>
      consultations.map(c =>
        c.id === id ? { ...c, ...updates, updatedAt: new Date() } : c
      )
    );
  }
}

